import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * WATCH-TOWER (Дозорная башня) — the guard LOOKOUT of Castle Hearthhold: a tall, narrow vigil-post where a
 * single sentry keeps the night watch. The HERO is the LOOKOUT PERCH near the top — a lone watch-stool drawn
 * up to a small MAP TABLE, the one spot the whole room orients toward. The mood is deliberately DIM and
 * vigilant: ONE low BRAZIER in the south-west corner is the room's only warm pool, throwing a long honeyed
 * glow up otherwise-cold stone (no rival lights — the dimness is the point). A silent SUIT OF ARMOUR stands
 * eternal sentinel, a WEAPON RACK waits within a hand's reach of the door, and a watch BANNER over the perch
 * with a single heraldic SHIELD on the west wall keep the dressing sparse — a manned tower, not a museum.
 *
 * Composition: hero perch high under the banner → eye drawn up; sentinel + shield anchor the west wall; the
 * rack hugs the east; the lone brazier weights the south-west corner. Wide breathing room is intentional.
 *
 * Geometry: zone (40,40) 320×720 → centre (200,400). Doors: E wall @ y200 (to the rampart), S wall area to
 * the armoury. The central vertical lane + both door mouths are kept walkable; set-dressing hugs the walls
 * and the perch sits high, off the through-path.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── HERO: the LOOKOUT PERCH — a watch-stool drawn up to the map table near the top of the tower. ──
  // The stool IS the sit anchor (200,200); the small map table sits just below it (200,260).
  place(ctx, kit.smallTable(art, 30), 200, 260); // the lookout's map table (table first, stool over it)
  place(ctx, kit.woodStool(art), 200, 200); // the watch perch — the room's focal seat

  // ── A watch BANNER hung high above the perch — the eye is drawn up to the lookout. ──
  place(ctx, kit.banner(art, 0x2f4a8a, 84), 200, 120);

  // ── The silent SENTINEL — a suit of armour standing eternal watch against the west wall. ──
  place(ctx, kit.suitOfArmour(art), 120, 520);

  // ── The WEAPON RACK on the east wall — arms within a sentry's reach (clear of the y200 door mouth). ──
  place(ctx, kit.weaponRack(art), 290, 420);

  // ── The lone BRAZIER, south-west corner — the room's ONLY warm pool, throwing a long glow up the cold
  //    tower. Deliberately the single light source: the dimness sells the night-watch mood. ──
  place(ctx, kit.brazier(art), 110, 640);

  // ── A single heraldic SHIELD on the west wall — the watch's colours, one accent, no wall-clutter. ──
  place(ctx, kit.shield(art, 0x8a2f2f), 70, 350);
};

export default decorate;
